/**
 * Wave CF — case type picker.
 *
 * When the firm has set up court forms, offers the controlled case types for its
 * practice areas (grouped), which drive the court-form packet. Free text is still
 * accepted for anything else, and an unconfigured firm gets the plain text box it
 * had before — so existing matters with free-text types keep working.
 */
import React from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getAuthToken } from '../../lib/authToken';

type CaseTypeOption = { key: string; label: string; practiceArea: string; jurisdiction: string };

const AREA_LABEL: Record<string, string> = {
  immigration: 'Immigration',
  family: 'Family law',
  guardianship: 'Guardianship',
  juvenile: 'Juvenile',
  appellate: 'Appeals',
  criminal: 'Criminal',
  estate_planning: 'Estate planning',
  personal_injury: 'Personal injury',
  civil: 'Civil litigation',
  general: 'General',
};

interface CaseTypePickerProps {
  value: string;
  caseTypeKey: string;
  onChange: (label: string, key: string) => void;
  disabled?: boolean;
}

const CaseTypePicker: React.FC<CaseTypePickerProps> = ({ value, caseTypeKey, onChange, disabled }) => {
  const { data } = useQuery({
    queryKey: ['court-forms', 'case-types'],
    queryFn: async () => {
      const res = await fetch('/api/court-forms/case-types', { headers: { Authorization: `Bearer ${getAuthToken()}` } });
      if (!res.ok) return { configured: false, caseTypes: [] };
      return (await res.json()).data;
    },
    staleTime: 5 * 60 * 1000,
  });
  const options: CaseTypeOption[] = data?.caseTypes || [];

  if (!data?.configured || !options.length) {
    return (
      <TextField
        fullWidth
        label="Case Type"
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value, '')}
      />
    );
  }

  const selected = options.find((o) => o.key === caseTypeKey) || null;
  return (
    <Autocomplete<CaseTypeOption, false, false, true>
      freeSolo
      disabled={disabled}
      options={options}
      groupBy={(o) => `${AREA_LABEL[o.practiceArea] || o.practiceArea}${o.jurisdiction === 'US-FED' ? '' : ` — ${o.jurisdiction}`}`}
      getOptionLabel={(o) => (typeof o === 'string' ? o : o.label)}
      value={selected || value || null}
      onChange={(_, v) => {
        if (!v) onChange('', '');
        else if (typeof v === 'string') onChange(v, '');
        else onChange(v.label, v.key);
      }}
      onInputChange={(_, text, reason) => {
        // Typing over a chosen type turns it back into free text (no packet).
        if (reason === 'input' && (!selected || text !== selected.label)) onChange(text, '');
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Case Type"
          helperText={selected
            ? 'Court forms for this case type are added to the Matter Folder as drafts for attorney review.'
            : 'Choose a case type to add its court forms automatically, or type your own.'}
        />
      )}
    />
  );
};

export default CaseTypePicker;
